import type { ServicePageData } from "$lib/types/service-detail";

const backgroundRemoveMedia = {
  heroProduct: {
    src: "/images/background-remove/background-remove-hero-scandinavian-round-stool.webp",
    alt: "Light wood Scandinavian round stool isolated against a clean background",
    width: 456,
    height: 570,
  },
  heroCosmetics: {
    src: "/images/background-remove/background-remove-hero-gold-stud-earrings.webp",
    alt: "Gold stud earrings isolated for a clean product presentation",
    width: 1600,
    height: 2000,
  },
  heroApparel: {
    src: "/images/background-remove/background-remove-hero-black-trucker-hat.webp",
    alt: "Black trucker hat isolated for a clean product presentation",
    width: 1600,
    height: 2000,
  },
  introCatalogExamples: {
    src: "/videos/background-remove-intro-cutout-workflow.mp4",
    alt: "Background removal video demonstration showing hand-drawn pen paths and subject extraction",
    width: 576,
    height: 720,
  },
  comparisonOriginal: {
    src: "/images/background-remove/background-remove-comparison-cosmetic-bottle-after.webp",
    alt: "Fashion model on a clean studio background before background removal",
    width: 500,
    height: 625,
  },
  comparisonCutout: {
    src: "/images/background-remove/background-remove-comparison-cosmetic-bottle-before.webp",
    alt: "The same fashion model isolated over a transparent checkerboard after background removal",
    width: 498,
    height: 622,
  },
  maskingBefore: {
    src: "/images/background-remove/background-remove-masking-model-portrait-before.webp",
    alt: "Fashion model portrait before background removal and alpha masking",
    width: 1600,
    height: 2000,
  },
  maskingAfter: {
    src: "/images/background-remove/background-remove-masking-model-portrait-after.webp",
    alt: "Fashion model portrait after background removal with hair and alpha masking",
    width: 1600,
    height: 2000,
  },
  industrialRackBefore: {
    src: "/images/background-remove/services-showcase-industrial-rack-retouch-before.webp",
    alt: "Industrial machinery server rack before background removal",
    width: 1506,
    height: 1882,
  },
  industrialRackAfter: {
    src: "/images/background-remove/services-showcase-industrial-rack-retouch-after.webp",
    alt: "Industrial machinery server rack cleanly isolated with precision cutouts and internal gap knockouts",
    width: 1506,
    height: 1882,
  },
  autoPartGrille: {
    src: "/images/background-remove/background-remove-autopart-fog-grille.webp",
    alt: "Automotive front fog grille and intake bezel isolated on pure white background",
    width: 1600,
    height: 2000,
  },
  autoPartHeadlamp: {
    src: "/images/background-remove/background-remove-autopart-headlamp.webp",
    alt: "Automotive headlamp assembly with clear lens and internal reflector isolated on pure white",
    width: 1600,
    height: 2000,
  },
} as const;

export const backgroundRemovePage: ServicePageData = {
  slug: "background-remove",
  seo: {
    title: "Background Removal Service | Studio Click House",
    description:
      "Product and fashion background removal with hand-drawn paths, alpha masks, and natural shadows for e-commerce, advertising, and print.",
  },
  hero: {
    title: "Background",
    titleAccent: "Removal.",
    theme: "light",
    titleWidth: "wide",
    mediaFit: "cover",
    aspectRatio: "4/5",
    description:
      "Isolate products, models, and complex objects with clean edges. We use hand-drawn clipping paths and alpha masks for white, transparent, or custom backgrounds.",
    media: backgroundRemoveMedia.heroProduct,
    supportingMedia: [
      backgroundRemoveMedia.heroCosmetics,
      backgroundRemoveMedia.heroApparel,
    ],
  },
  intro: {
    heading: "What is professional background removal?",
    paragraphs: [
      "Background removal separates your subject from distracting surroundings, creating clean, versatile assets for e-commerce catalogs, advertising campaigns, and print packaging. Our artists use hand-drawn Photoshop pen paths and multi-channel alpha masking to preserve the finest details—from stray hair and soft fur to transparent glassware and delicate lace.",
    ],
    stages: [
      {
        label: "Hand-Drawn Vector Path",
        description:
          "Our artists trace every curve manually using Photoshop's Pen Tool for razor-sharp, natural outlines.",
        media: backgroundRemoveMedia.introCatalogExamples,
      },
      {
        label: "Alpha Channel Hair Masking",
        description:
          "Soft edges, wool textures, and wispy hair are separated with multi-channel color masking.",
      },
      {
        label: "Shadow & Reflection Synthesis",
        description:
          "We add natural drop shadows, cast reflections, or pure transparency suited for marketplace standards.",
      },
    ],
  },
  beforeAfter: {
    heading: "Product background removal before and after",
    description:
      "Compare the original photo with the isolated result, with clean edges and a natural contact shadow.",
    bullets: [
      "Isolate the subject while preserving fine edges",
      "Keep a natural contact shadow when needed",
      "Prepare clean cutouts for catalogs and campaigns",
    ],
    beforeSrc: backgroundRemoveMedia.comparisonOriginal.src,
    beforeAlt: backgroundRemoveMedia.comparisonOriginal.alt,
    afterSrc: backgroundRemoveMedia.comparisonCutout.src,
    afterAlt: backgroundRemoveMedia.comparisonCutout.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    width: backgroundRemoveMedia.comparisonCutout.width,
    height: backgroundRemoveMedia.comparisonCutout.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "Furniture background removal for a clean catalog cutout",
    description:
      "See the same wooden spindle chair isolated from its original studio setting for a clean catalog presentation.",
    bullets: [
      "Keep the chair shape and wood finish consistent",
      "Separate the spindle, seat, and leg details cleanly",
      "Prepare a polished white-background product image",
    ],
    theme: "light",
    beforeAfter: {
      before: {
        src: "/images/background-remove/background-remove-showcase-wooden-spindle-chair-before.webp",
        alt: "Wooden spindle chair photographed against its original studio background",
        width: 1600,
        height: 2000,
        label: "Before",
      },
      after: {
        src: "/images/background-remove/background-remove-showcase-wooden-spindle-chair-after.webp",
        alt: "The same wooden spindle chair isolated on a clean white background",
        width: 1122,
        height: 1402,
        label: "After",
      },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "background-remove-masking-comparison",
      heading: "Background removal with masking",
      description:
        "Preserve fine flyaway hair, soft edges, and intricate details with multi-channel alpha masking and precision hand-drawn pen paths.",
      bullets: [
        "Retain fine hair strands and subtle transparent edges",
        "Clean extraction with zero halos or jagged borders",
        "Ready for transparent, pure white, or editorial backgrounds",
      ],
      beforeSrc: backgroundRemoveMedia.maskingBefore.src,
      beforeAlt: backgroundRemoveMedia.maskingBefore.alt,
      afterSrc: backgroundRemoveMedia.maskingAfter.src,
      afterAlt: backgroundRemoveMedia.maskingAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: backgroundRemoveMedia.maskingAfter.width,
      height: backgroundRemoveMedia.maskingAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "background-remove-industrial-comparison",
      heading: "Complex industrial equipment & machinery cutouts",
      description:
        "Isolate intricate industrial equipment, server racks, and mechanical assemblies with multiple internal gap knockouts, clean pipe boundaries, and zero background bleed.",
      bullets: [
        "Cut out dozens of internal frame openings and intricate hardware gaps",
        "Maintain sharp metallic contours and structural geometric lines",
        "Supplied as multi-layer PSDs or clean transparent PNGs for catalog publishing",
      ],
      beforeSrc: backgroundRemoveMedia.industrialRackBefore.src,
      beforeAlt: backgroundRemoveMedia.industrialRackBefore.alt,
      afterSrc: backgroundRemoveMedia.industrialRackAfter.src,
      afterAlt: backgroundRemoveMedia.industrialRackAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: backgroundRemoveMedia.industrialRackAfter.width,
      height: backgroundRemoveMedia.industrialRackAfter.height,
      layout: "cards",
      textPosition: "left",
    },
  ],
  partsShowcase: {
    sectionId: "automotive-parts-cutouts",
    eyebrow: "Technical Scope",
    heading: "Precision cutouts for automotive & industrial parts.",
    description:
      "Automotive aftermarket retailers, parts distributors, and technical catalogs demand razor-sharp edge definition. We isolate multi-vent honeycomb grilles, complex window regulators, and metallic assemblies with multi-path internal knockouts.",
    bullets: [
      "Isolate intricate internal honeycomb slots and bracket gaps",
      "Maintain crisp plastic contours, chrome reflections, and cable wiring",
      "Delivered on pure white RGB (255, 255, 255) or transparent alpha",
    ],
    items: [
      {
        id: "fog-grille-cutout",
        label: "Auto Grille",
        title: "Front Bumper Grille",
        media: backgroundRemoveMedia.autoPartGrille,
      },
      {
        id: "window-regulator-cutout",
        label: "Motor Assembly",
        title: "Window Regulator & Motor",
        media: backgroundRemoveMedia.autoPartHeadlamp,
      },
    ],
    textPosition: "right",
  },
  features: {
    heading: "Why brands trust our cutout pipeline",
    items: [
      {
        icon: "Scissors",
        title: "100% Hand-Drawn Pen Tool Paths",
        description:
          "We never rely on jagged AI auto-selectors. Every clipping path is drawn by hand by seasoned Photoshop artists zoomed to 300%.",
      },
      {
        icon: "Layers",
        title: "Transparent & Layered Delivery",
        description:
          "Receive files in layered PSD, TIFF with embedded paths, transparent PNG, or marketplace-compliant pure white sRGB JPEGs.",
      },
      {
        icon: "Sun",
        title: "Natural Drop & Cast Shadows",
        description:
          "Ground your products with custom drop shadows, original contact shadows, or reflective mirror surfaces.",
      },
      {
        icon: "ShieldCheck",
        title: "Two-Tier Quality Control",
        description:
          "Every file is reviewed by both the production artist and a quality control supervisor before client delivery.",
      },
      {
        icon: "Zap",
        title: "Overnight Turnaround Available",
        description:
          "Take advantage of our 24/7 multi-shift production floor to deliver high-volume batches overnight with zero SLA breach.",
      },
      {
        icon: "BadgeDollarSign",
        title: "Volume Tier Discounts",
        description:
          "Enjoy transparent per-image pricing with substantial discounts on bulk catalogs and seasonal collection drops.",
      },
    ],
  },
  productTypes: [
    {
      id: "on-model-apparel",
      code: "01",
      title: "On-Model Apparel",
      subtitle: "Fashion cutouts with clean edges around the model and garment.",
      src: "/images/background-remove/background-remove-product-blue-jumpsuit-model.webp",
      alt: "Model wearing a blue jumpsuit isolated on a white background",
    },
    {
      id: "leather-handbag",
      code: "02",
      title: "Leather Handbags",
      subtitle: "Preserve leather grain, stitching, and polished hardware.",
      src: "/images/background-remove/background-remove-product-emerald-leather-handbag.webp",
      alt: "Emerald green leather handbag isolated on a white background",
    },
    {
      id: "footwear",
      code: "03",
      title: "Footwear",
      subtitle: "Clean isolation for sneaker panels, laces, and soles.",
      src: "/images/background-remove/background-remove-product-beige-leather-sneakers.webp",
      alt: "Pair of beige and white sneakers isolated on a white background",
    },
    {
      id: "watches",
      code: "04",
      title: "Watches & Jewelry",
      subtitle: "Careful edge work for reflective metal and fine details.",
      src: "/images/background-remove/background-remove-product-rose-gold-luxury-watch.webp",
      alt: "Rose gold wristwatch isolated on a white background",
    },
    {
      id: "furniture",
      code: "05",
      title: "Furniture & Home",
      subtitle: "Define upholstery, curved forms, and furniture legs cleanly.",
      src: "/images/background-remove/background-remove-product-olive-accent-chair.webp",
      alt: "Olive green upholstered accent chair isolated on a white background",
    },
    {
      id: "denim",
      code: "06",
      title: "Denim Apparel",
      subtitle: "Keep denim weave, stitching, and garment shape clear.",
      src: "/images/background-remove/background-remove-product-flatlay-denim-jacket.webp",
      alt: "Folded dark denim jacket isolated on a white background",
    },
  ],
  faqs: [
    {
      question: "How do you handle complex hair, fur, and transparent objects?",
      answer:
        "We use advanced multi-channel color masking and alpha matting combined with hand-drawn pen paths. This allows us to cleanly extract fine flyaway hairs, animal fur, transparent glassware, and tulle fabrics without losing edge softness.",
    },
    {
      question: "Which file formats can I deliver and receive?",
      answer:
        "We accept RAW, PSD, PSB, TIFF, PNG, and JPEG formats. Delivered files can be provided as layered PSDs with active vector paths, transparent PNGs, or optimized pure white JPEGs according to your specifications.",
    },
    {
      question: "Can you keep the original natural shadow of the product?",
      answer:
        "Yes. We can retain the original natural studio shadow, create a soft directional drop shadow, or generate a realistic reflective surface beneath the product.",
    },
    {
      question: "What is your turnaround time for high-volume orders?",
      answer:
        "Standard orders of up to 500 images are delivered within 24 hours. Express 12-hour turnaround is available for time-sensitive releases. For orders above 2,000 images, we establish dedicated production schedules.",
    },
    {
      question: "Do you offer free test edits?",
      answer:
        "Yes, we provide free trial edits for up to 3 sample images so you can evaluate our path precision and quality before placing a full batch order.",
    },
  ],
  cta: {
    heading: "Ready for pixel-perfect background removal?",
    description:
      "Send us a sample batch or describe your catalog guidelines. Our dedicated production team will deliver a complimentary test edit and custom volume quote within hours.",
    steps: [
      {
        title: "Send test images",
        description:
          "Upload up to 3 sample files and specify your background requirements.",
      },
      {
        title: "Review trial cutouts",
        description:
          "Inspect our hand-drawn path precision and edge quality with zero commitment.",
      },
      {
        title: "Scale your batch",
        description:
          "Submit full catalog volumes and receive overnight deliveries through secure transfer.",
      },
    ],
  },
};
